import React, { useEffect, useState } from 'react';
import { api } from '../../services/api';
import { Search, Save, User, Key, CheckCircle, AlertCircle, Trash2, Pencil, Plus, X } from 'lucide-react';

const LICENSE_CATEGORIES = ['A1', 'A2', 'B1', 'B2', 'B3', 'C1', 'C2', 'C3'];

const emptyForm = {
  person_id: '',
  nombre: '',
  usuario: '',
  password: '',
  cedula: '',
  cargo: '',
  area: '',
  telefono: '',
  correo: '',
  estado: '',
  fecha_ingreso: '',
  tipo_contrato: '',
  licencias: [],
  tiene_multas: false,
  multas: [],
  tiene_certificado_altura: false,
  certificado_altura_vigencia: '',
};

// Semaforización de vigencia (igual criterio que Gestión de Flota): vencido
// (rojo), vence en 30 días o menos (ámbar), vigente (verde).
const EXPIRY_WARNING_DAYS = 30;
function getExpiryStatus(dateStr) {
  if (!dateStr) return { text: '-', dot: 'bg-gray-300', textClass: 'text-gray-400' };
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(`${dateStr}T00:00:00`);
  if (Number.isNaN(target.getTime())) return { text: dateStr, dot: 'bg-gray-300', textClass: 'text-gray-400' };
  const diffDays = Math.round((target - today) / (1000 * 60 * 60 * 24));
  if (diffDays < 0) return { text: dateStr, dot: 'bg-red-500', textClass: 'text-red-600 font-semibold' };
  if (diffDays <= EXPIRY_WARNING_DAYS) return { text: dateStr, dot: 'bg-amber-500', textClass: 'text-amber-600 font-semibold' };
  return { text: dateStr, dot: 'bg-green-500', textClass: 'text-green-700' };
}

function ExpiryDot({ date }) {
  const s = getExpiryStatus(date);
  return (
    <span className={`inline-flex items-center gap-1.5 ${s.textClass}`}>
      <span className={`h-2 w-2 rounded-full ${s.dot}`} />
      {s.text}
    </span>
  );
}

function parseJsonArray(value) {
  if (Array.isArray(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch (_) {
      return [];
    }
  }
  return [];
}

function firstRow(res) {
  if (!res?.ok) return null;
  if (Array.isArray(res.data)) return res.data[0] || null;
  if (res.data && typeof res.data === 'object') return res.data;
  return null;
}

function looksLikeVehiclePlate(value) {
  const token = String(value || '').replace(/[\s.\-]+/g, '').toUpperCase();
  if (!token || /^\d{6,12}$/.test(token)) return false;
  return /^[A-Z]{3}\d{3}$/.test(token)
    || /^[A-Z]{3}\d{2}[A-Z0-9]$/.test(token)
    || /^[A-Z]{2,3}\d{2,3}[A-Z0-9]?$/.test(token);
}

export default function ConductoresModule() {
  const [activeTab, setActiveTab] = useState('list');
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [cedulaSearch, setCedulaSearch] = useState('');
  const [foundPerson, setFoundPerson] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    if (activeTab === 'list') fetchDrivers();
  }, [activeTab]);

  const fetchDrivers = async () => {
    setLoading(true);
    try {
      const res = await api.conductores.get();
      if (res?.ok) setDrivers(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const searchPerson = async () => {
    if (!cedulaSearch.trim()) return;
    if (looksLikeVehiclePlate(cedulaSearch)) {
      setMessage({
        type: 'error',
        text: 'Ese valor parece una placa de vehículo. Regístrela en Transportes > Flota; aquí solo se vinculan conductores (personas).',
      });
      return;
    }
    setLoading(true);
    setFoundPerson(null);
    setMessage(null);

    try {
      let peopleRes = await api.people.get({ dni: cedulaSearch, clean: 1, limit: 1 });
      let hrRes = await api.hr.employees.get({ dni: cedulaSearch, limit: 1 });

      let person = firstRow(peopleRes);
      let hr = firstRow(hrRes);

      if (!person) {
        peopleRes = await api.people.get({ q: cedulaSearch, clean: 1, limit: 10 });
        const peopleRows = Array.isArray(peopleRes?.data) ? peopleRes.data : [];
        person = peopleRows.find((item) => String(item?.dni || item?.id || '') === String(cedulaSearch)) || peopleRows[0] || null;
      }

      if (!hr) {
        hrRes = await api.hr.employees.get({ q: cedulaSearch, limit: 10 });
        const hrRows = Array.isArray(hrRes?.data) ? hrRes.data : [];
        hr = hrRows.find((item) => String(item?.dni || '') === String(cedulaSearch)) || hrRows[0] || null;
      }

      if (!person && !hr) {
        setMessage({ type: 'error', text: 'No se encontró información en People ni en Talento Humano para esa cédula.' });
        setFormData({
          ...emptyForm,
          cedula: cedulaSearch,
          usuario: cedulaSearch,
        });
        return;
      }

      const docId = String(person?.dni || person?.id || hr?.dni || cedulaSearch || '');

      // Cédula + usuario son UNIQUE en conductores_app: si ya existe un
      // conductor con esta cédula, intentar "crear" revienta la restricción
      // y el usuario solo veía "Error de conexión" sin ninguna pista. Se
      // detecta acá y se pasa a edición del registro existente en vez de
      // dejar que el guardado falle a ciegas.
      let existingDriver = null;
      try {
        const driversRes = await api.conductores.get({ q: docId });
        const driverRows = Array.isArray(driversRes?.data) ? driversRes.data : [];
        existingDriver = driverRows.find((d) => String(d?.cedula || '') === docId || String(d?.usuario || '') === docId) || null;
      } catch (_) {
        // Si esta consulta falla, seguimos con el flujo normal de creación;
        // si de verdad había un duplicado, el backend lo reportará al guardar.
      }

      const merged = existingDriver
        ? {
            ...emptyForm,
            person_id: person?._dbId || hr?.person_id || existingDriver.person_id || '',
            nombre: person?.name || hr?.name || existingDriver.nombre || '',
            usuario: existingDriver.usuario || docId,
            password: '',
            cedula: existingDriver.cedula || docId,
            cargo: person?.position || hr?.position || existingDriver.cargo || '',
            area: person?.area || hr?.department || existingDriver.area || '',
            telefono: person?.phone || hr?.phone || existingDriver.telefono || '',
            correo: person?.email || hr?.email || existingDriver.correo || '',
            estado: person?.status || hr?.status || existingDriver.estado || '',
            fecha_ingreso: person?.hireDate || hr?.hire_date || existingDriver.fecha_ingreso || '',
            tipo_contrato: hr?.contract_type || existingDriver.tipo_contrato || '',
            licencias: parseJsonArray(existingDriver.licencias_json),
            tiene_multas: !!Number(existingDriver.tiene_multas),
            multas: parseJsonArray(existingDriver.multas_json),
            tiene_certificado_altura: !!Number(existingDriver.tiene_certificado_altura),
            certificado_altura_vigencia: existingDriver.certificado_altura_vigencia || '',
          }
        : {
            // Campos propios del conductor (licencias, multas, certificado de
            // alturas) no existen en People ni en Talento Humano — se dejan en
            // blanco como en emptyForm, en vez de omitirlos: omitirlos dejaba
            // formData.licencias en undefined y el checklist de licencias
            // reventaba al hacer formData.licencias.includes(cat).
            ...emptyForm,
            person_id: person?._dbId || hr?.person_id || '',
            nombre: person?.name || hr?.name || '',
            usuario: docId,
            password: '',
            cedula: docId,
            cargo: person?.position || hr?.position || '',
            area: person?.area || hr?.department || '',
            telefono: person?.phone || hr?.phone || '',
            correo: person?.email || hr?.email || '',
            estado: person?.status || hr?.status || '',
            fecha_ingreso: person?.hireDate || hr?.hire_date || '',
            tipo_contrato: hr?.contract_type || '',
          };

      setEditingId(existingDriver ? existingDriver.id : null);
      setFoundPerson({ person, hr });
      setFormData(merged);
      setMessage(existingDriver
        ? { type: 'success', text: 'Ya existe un conductor con esta cédula — se cargaron sus datos para editarlo (no se creará uno duplicado).' }
        : {
            type: 'success',
            text: `Datos encontrados${person ? ' en People' : ''}${person && hr ? ' y' : ''}${hr ? ' en Talento Humano' : ''}.`,
          });
    } catch (error) {
      console.error(error);
      setMessage({ type: 'error', text: 'No fue posible consultar People/Talento Humano.' });
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    const isEditing = !!editingId;
    if (!formData.nombre || !formData.usuario || !formData.cedula || (!isEditing && !formData.password)) {
      setMessage({ type: 'error', text: `Debes completar Nombre, Usuario, Cédula${isEditing ? '' : ' y Contraseña'}.` });
      return;
    }

    setLoading(true);
    setMessage(null);
    try {
      const payload = {
        ...formData,
        id: editingId || undefined,
        licencias: JSON.stringify(formData.licencias || []),
        multas: JSON.stringify(formData.multas || []),
        tiene_multas: formData.tiene_multas ? 1 : 0,
        tiene_certificado_altura: formData.tiene_certificado_altura ? 1 : 0,
      };
      const res = await api.conductores.upsert(payload);
      if (res?.ok) {
        setMessage({ type: 'success', text: isEditing ? 'Conductor actualizado correctamente.' : 'Conductor creado y complementado correctamente.' });
        setFormData(emptyForm);
        setCedulaSearch('');
        setFoundPerson(null);
        setEditingId(null);
        setTimeout(() => setActiveTab('list'), 1200);
      } else {
        setMessage({ type: 'error', text: res?.error || 'No fue posible guardar el conductor.' });
      }
    } catch (error) {
      console.error(error);
      // Antes siempre mostraba un genérico "Error de conexión" sin importar
      // la causa real (ej. cédula/usuario duplicados), dejando al usuario
      // sin ninguna pista. Se muestra el mensaje real cuando existe.
      const rawMsg = String(error?.message || '').replace(/^HTTP \d+:\s*/, '').trim();
      setMessage({ type: 'error', text: rawMsg || 'Error de conexión al guardar el conductor.' });
    } finally {
      setLoading(false);
    }
  };

  const openEdit = (driver) => {
    setEditingId(driver.id);
    setFoundPerson(null);
    setCedulaSearch('');
    setFormData({
      person_id: driver.person_id || '',
      nombre: driver.nombre || '',
      usuario: driver.usuario || '',
      password: '',
      cedula: driver.cedula || '',
      cargo: driver.cargo || '',
      area: driver.area || '',
      telefono: driver.telefono || '',
      correo: driver.correo || '',
      estado: driver.estado || '',
      fecha_ingreso: driver.fecha_ingreso || '',
      tipo_contrato: driver.tipo_contrato || '',
      licencias: parseJsonArray(driver.licencias_json),
      tiene_multas: !!Number(driver.tiene_multas),
      multas: parseJsonArray(driver.multas_json),
      tiene_certificado_altura: !!Number(driver.tiene_certificado_altura),
      certificado_altura_vigencia: driver.certificado_altura_vigencia || '',
    });
    setMessage(null);
    setActiveTab('create');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setFormData(emptyForm);
    setFoundPerson(null);
    setCedulaSearch('');
    setMessage(null);
  };

  const toggleLicencia = (cat) => {
    setFormData((prev) => ({
      ...prev,
      licencias: prev.licencias.includes(cat) ? prev.licencias.filter((c) => c !== cat) : [...prev.licencias, cat],
    }));
  };

  const addMulta = () => {
    setFormData((prev) => ({ ...prev, multas: [...prev.multas, { fecha: '', codigo: '' }] }));
  };

  const updateMulta = (idx, field, value) => {
    setFormData((prev) => ({
      ...prev,
      multas: prev.multas.map((m, i) => (i === idx ? { ...m, [field]: value } : m)),
    }));
  };

  const removeMulta = (idx) => {
    setFormData((prev) => ({ ...prev, multas: prev.multas.filter((_, i) => i !== idx) }));
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Está seguro de eliminar este conductor?')) return;
    setLoading(true);
    try {
      const res = await api.conductores.delete({ id });
      if (res?.ok) {
        fetchDrivers();
      } else {
        setMessage({ type: 'error', text: res?.error || 'No fue posible eliminar el conductor.' });
      }
    } catch (error) {
      console.error(error);
      setMessage({ type: 'error', text: 'Error al eliminar el conductor.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Gestión de Conductores</h2>
          <p className="mt-1 text-sm text-gray-500">Cruza datos con People y Talento Humano para completar automáticamente la base de transporte.</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => { setActiveTab('list'); cancelEdit(); }}
            className={`rounded-lg px-4 py-2 ${activeTab === 'list' ? 'text-white' : 'bg-gray-100 text-gray-600'}`}
            style={activeTab === 'list' ? { backgroundColor: '#F07E2D' } : undefined}
          >
            Lista
          </button>
          <button
            onClick={() => { cancelEdit(); setActiveTab('create'); }}
            className={`rounded-lg px-4 py-2 ${activeTab === 'create' ? 'text-white' : 'bg-gray-100 text-gray-600'}`}
            style={activeTab === 'create' ? { backgroundColor: '#F07E2D' } : undefined}
          >
            Nuevo Conductor
          </button>
        </div>
      </div>

      {message && (
        <div className={`mb-6 flex items-center gap-2 rounded-lg p-4 ${message.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
          {message.type === 'success' ? <CheckCircle size={20} /> : <AlertCircle size={20} />}
          {message.text}
        </div>
      )}

      {activeTab === 'create' && (
        <div className="mx-auto max-w-5xl rounded-xl border border-gray-100 bg-white p-6 shadow-sm">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-800">{editingId ? 'Editar conductor' : 'Nuevo conductor'}</h3>
            {editingId && (
              <button type="button" onClick={() => { cancelEdit(); setActiveTab('list'); }} className="text-sm text-gray-500 hover:text-gray-700">
                Cancelar edición
              </button>
            )}
          </div>
          <div className="mb-8">
            <label className="mb-2 block text-sm font-medium text-gray-700">Buscar por Cédula</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={cedulaSearch}
                onChange={(e) => setCedulaSearch(e.target.value)}
                placeholder="Ingrese número de documento"
                className="flex-1 rounded-lg border p-2 outline-none focus:ring-2 focus:ring-orange-400"
              />
              <button
                type="button"
                onClick={searchPerson}
                disabled={loading}
                className="flex items-center gap-2 rounded-lg bg-gray-800 px-4 py-2 text-white hover:bg-gray-700"
              >
                <Search size={18} /> Buscar
              </button>
            </div>
            <p className="mt-1 text-xs text-gray-500">Consulta People y Talento Humano para autocompletar los datos del conductor.</p>
          </div>

          <form onSubmit={handleCreate} className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Cédula</label>
                <input type="text" value={formData.cedula} onChange={(e) => setFormData((prev) => ({ ...prev, cedula: e.target.value }))} className="w-full rounded-lg border p-2" required />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Nombre completo</label>
                <input type="text" value={formData.nombre} onChange={(e) => setFormData((prev) => ({ ...prev, nombre: e.target.value }))} className="w-full rounded-lg border p-2" required />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Usuario</label>
                <input type="text" value={formData.usuario} onChange={(e) => setFormData((prev) => ({ ...prev, usuario: e.target.value }))} className="w-full rounded-lg border p-2" required />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Contraseña{editingId && <span className="font-normal text-gray-400"> (dejar en blanco para no cambiarla)</span>}</label>
                <div className="relative">
                  <Key className="absolute left-3 top-2.5 text-gray-400" size={18} />
                  <input type="password" value={formData.password} onChange={(e) => setFormData((prev) => ({ ...prev, password: e.target.value }))} className="w-full rounded-lg border p-2 pl-10" placeholder="Establecer contraseña" required={!editingId} />
                </div>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Cargo</label>
                <input type="text" value={formData.cargo} onChange={(e) => setFormData((prev) => ({ ...prev, cargo: e.target.value }))} className="w-full rounded-lg border p-2" />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Área</label>
                <input type="text" value={formData.area} onChange={(e) => setFormData((prev) => ({ ...prev, area: e.target.value }))} className="w-full rounded-lg border p-2" />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Teléfono</label>
                <input type="text" value={formData.telefono} onChange={(e) => setFormData((prev) => ({ ...prev, telefono: e.target.value }))} className="w-full rounded-lg border p-2" />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Correo</label>
                <input type="email" value={formData.correo} onChange={(e) => setFormData((prev) => ({ ...prev, correo: e.target.value }))} className="w-full rounded-lg border p-2" />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Estado</label>
                <input type="text" value={formData.estado} onChange={(e) => setFormData((prev) => ({ ...prev, estado: e.target.value }))} className="w-full rounded-lg border p-2" />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Fecha ingreso</label>
                <input type="date" value={formData.fecha_ingreso} onChange={(e) => setFormData((prev) => ({ ...prev, fecha_ingreso: e.target.value }))} className="w-full rounded-lg border p-2" />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Tipo contrato</label>
                <input type="text" value={formData.tipo_contrato} onChange={(e) => setFormData((prev) => ({ ...prev, tipo_contrato: e.target.value }))} className="w-full rounded-lg border p-2" />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">ID persona</label>
                <input type="text" value={formData.person_id} onChange={(e) => setFormData((prev) => ({ ...prev, person_id: e.target.value }))} className="w-full rounded-lg border bg-gray-50 p-2" readOnly={!!foundPerson} />
              </div>
            </div>

            <div className="rounded-lg border border-gray-200 p-4">
              <label className="mb-2 block text-sm font-medium text-gray-700">Licencias de conducción (puede tener varias)</label>
              <div className="flex flex-wrap gap-2">
                {LICENSE_CATEGORIES.map((cat) => {
                  const active = formData.licencias.includes(cat);
                  return (
                    <button
                      key={cat}
                      type="button"
                      onClick={() => toggleLicencia(cat)}
                      className={`rounded-full border px-3 py-1.5 text-sm font-semibold transition-colors ${
                        active ? 'border-orange-500 bg-orange-500 text-white' : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      {cat}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="rounded-lg border border-gray-200 p-4">
              <div className="mb-2 flex items-center justify-between">
                <label className="text-sm font-medium text-gray-700">¿Cuenta con multas de tránsito?</label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setFormData((prev) => ({ ...prev, tiene_multas: true }))}
                    className={`rounded-lg px-3 py-1 text-sm font-semibold ${formData.tiene_multas ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-500'}`}
                  >
                    Sí
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData((prev) => ({ ...prev, tiene_multas: false, multas: [] }))}
                    className={`rounded-lg px-3 py-1 text-sm font-semibold ${!formData.tiene_multas ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}
                  >
                    No
                  </button>
                </div>
              </div>
              {formData.tiene_multas && (
                <div className="space-y-2">
                  {formData.multas.map((m, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <input
                        type="date"
                        value={m.fecha}
                        onChange={(e) => updateMulta(idx, 'fecha', e.target.value)}
                        className="rounded-lg border p-2 text-sm"
                      />
                      <input
                        type="text"
                        value={m.codigo}
                        onChange={(e) => updateMulta(idx, 'codigo', e.target.value)}
                        placeholder="Código de infracción (ej. C25)"
                        className="flex-1 rounded-lg border p-2 text-sm"
                      />
                      <button type="button" onClick={() => removeMulta(idx)} className="rounded-full p-1.5 text-red-500 hover:bg-red-50">
                        <X size={16} />
                      </button>
                    </div>
                  ))}
                  <button type="button" onClick={addMulta} className="flex items-center gap-1 text-sm font-semibold text-orange-600 hover:text-orange-700">
                    <Plus size={16} /> Agregar multa
                  </button>
                </div>
              )}
            </div>

            <div className="rounded-lg border border-gray-200 p-4">
              <div className="mb-2 flex items-center justify-between">
                <label className="text-sm font-medium text-gray-700">¿Cuenta con certificado de trabajo en alturas?</label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setFormData((prev) => ({ ...prev, tiene_certificado_altura: true }))}
                    className={`rounded-lg px-3 py-1 text-sm font-semibold ${formData.tiene_certificado_altura ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}
                  >
                    Sí
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData((prev) => ({ ...prev, tiene_certificado_altura: false, certificado_altura_vigencia: '' }))}
                    className={`rounded-lg px-3 py-1 text-sm font-semibold ${!formData.tiene_certificado_altura ? 'bg-gray-200 text-gray-700' : 'bg-gray-100 text-gray-500'}`}
                  >
                    No
                  </button>
                </div>
              </div>
              {formData.tiene_certificado_altura && (
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-500">Fecha de vigencia del certificado</label>
                  <input
                    type="date"
                    value={formData.certificado_altura_vigencia}
                    onChange={(e) => setFormData((prev) => ({ ...prev, certificado_altura_vigencia: e.target.value }))}
                    className="w-full max-w-xs rounded-lg border p-2 text-sm"
                  />
                </div>
              )}
            </div>

            <div className="pt-4">
              <button type="submit" disabled={loading} className="flex w-full items-center justify-center gap-2 rounded-lg py-2 text-white" style={{ backgroundColor: '#F07E2D' }}>
                <Save size={18} />
                {loading ? 'Guardando...' : editingId ? 'Guardar cambios' : 'Crear Conductor'}
              </button>
            </div>
          </form>
        </div>
      )}

      {activeTab === 'list' && (
        <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
          <table className="w-full text-left">
            <thead className="border-b border-gray-100 bg-gray-50">
              <tr>
                <th className="p-4 font-medium text-gray-600">Conductor</th>
                <th className="p-4 font-medium text-gray-600">Cargo / Área</th>
                <th className="p-4 font-medium text-gray-600">Contacto</th>
                <th className="p-4 font-medium text-gray-600">Estado</th>
                <th className="p-4 font-medium text-gray-600">Usuario</th>
                <th className="p-4 font-medium text-gray-600">Licencias / Multas / Alturas</th>
                <th className="p-4 text-right font-medium text-gray-600">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="7" className="p-8 text-center text-gray-500">Cargando conductores...</td></tr>
              ) : drivers.length === 0 ? (
                <tr><td colSpan="7" className="p-8 text-center text-gray-500">No hay conductores registrados.</td></tr>
              ) : (
                drivers.map((driver) => {
                  const licencias = parseJsonArray(driver.licencias_json);
                  const multas = parseJsonArray(driver.multas_json);
                  const hasMultas = !!Number(driver.tiene_multas);
                  const hasAltura = !!Number(driver.tiene_certificado_altura);
                  return (
                    <tr key={driver.id} className="border-b border-gray-50 hover:bg-gray-50">
                      <td className="p-4 text-gray-800">
                        <div className="flex items-center gap-2 font-medium">
                          <User size={16} className="text-gray-400" />
                          {driver.nombre}
                        </div>
                        <div className="mt-1 text-xs text-gray-500">Cédula: {driver.cedula || '-'}</div>
                      </td>
                      <td className="p-4 text-gray-600">
                        <div>{driver.cargo || '-'}</div>
                        <div className="mt-1 text-xs text-gray-500">{driver.area || '-'}</div>
                      </td>
                      <td className="p-4 text-gray-600">
                        <div>{driver.telefono || '-'}</div>
                        <div className="mt-1 text-xs text-gray-500">{driver.correo || '-'}</div>
                      </td>
                      <td className="p-4 text-gray-600">
                        <div>{driver.estado || '-'}</div>
                        <div className="mt-1 text-xs text-gray-500">Ingreso: {driver.fecha_ingreso || '-'}</div>
                      </td>
                      <td className="p-4 text-gray-600">
                        <div>{driver.usuario || '-'}</div>
                        <div className="mt-1 text-xs text-gray-500">{driver.tipo_contrato || '-'}</div>
                      </td>
                      <td className="p-4 text-xs">
                        <div className="mb-1 flex flex-wrap gap-1">
                          {licencias.length === 0 ? (
                            <span className="text-gray-400">Sin licencias</span>
                          ) : (
                            licencias.map((cat) => (
                              <span key={cat} className="rounded-full bg-gray-100 px-2 py-0.5 font-semibold text-gray-700">{cat}</span>
                            ))
                          )}
                        </div>
                        <div className={`mb-1 ${hasMultas ? 'text-red-600 font-semibold' : 'text-gray-400'}`}>
                          {hasMultas ? `Multas: ${multas.length}` : 'Sin multas'}
                        </div>
                        <div>
                          {hasAltura ? (
                            <>Alturas: <ExpiryDot date={driver.certificado_altura_vigencia} /></>
                          ) : (
                            <span className="text-gray-400">Sin certificado de alturas</span>
                          )}
                        </div>
                      </td>
                      <td className="p-4 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => openEdit(driver)} className="rounded-full p-2 text-blue-600 hover:bg-blue-50" title="Editar conductor">
                            <Pencil size={18} />
                          </button>
                          <button onClick={() => handleDelete(driver.id)} className="rounded-full p-2 text-red-500 hover:bg-red-50" title="Eliminar usuario">
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
